import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Loader2, ChevronRight } from 'lucide-react';
import { fetchAvailableDates, fetchTimeSlotAvailability } from './hooks/useStoreApi';
import { useVocab, useIsAppointments } from './hooks/useVocab';

export const TimeSlotSelection = ({ storeSlug, onComplete, onSkip }) => {
  const t = useVocab();
  const isAppt = useIsAppointments();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlotsEnabled, setTimeSlotsEnabled] = useState(false);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [loadingDates, setLoadingDates] = useState(true);
  const [availableDates, setAvailableDates] = useState([]);
  const [error, setError] = useState('');

  // Fetch available dates on mount
  useEffect(() => {
    if (!storeSlug) return;

    const loadAvailableDates = async () => {
      setLoadingDates(true);
      try {
        const result = await fetchAvailableDates(storeSlug, 7);

        if (!result.enabled) {
          // If timeslots are disabled, skip this step entirely
          setAvailableDates([]);
          setLoadingDates(false);
          if (onSkip) {
            onSkip();
          }
          return;
        }

        const datesWithSlots = result.available_dates.map(d => ({
          value: d.date,
          label: d.label
        }));

        setAvailableDates(datesWithSlots);

        // Auto-select first available date if only one
        if (datesWithSlots.length === 1) {
          setSelectedDate(datesWithSlots[0].value);
        }
      } catch (err) {
        console.error('Error fetching available dates:', err);
        setAvailableDates([]);
      } finally {
        setLoadingDates(false);
      }
    };

    loadAvailableDates();
  }, [storeSlug, onSkip]);

  // Fetch time slot availability when date is selected
  useEffect(() => {
    if (!selectedDate || !storeSlug) return;

    const fetchSlots = async () => {
      setLoadingTimeSlots(true);
      setError('');
      try {
        const result = await fetchTimeSlotAvailability(storeSlug, selectedDate);
        // Be resilient to different payload shapes
        setTimeSlotsEnabled(
          result.enabled ?? result.time_slots_enabled ?? false
        );
        const slots = result.slots || result.available_slots || [];
        setTimeSlots(slots);
        // Auto-select if only one available slot
        const only = slots.length === 1 ? slots[0] : null;
        const onlyAvailable = only
          ? (
            (only.available !== undefined ? only.available : (
              (typeof only.booked_orders === 'number' && typeof only.max_orders === 'number' && only.max_orders > 0)
                ? (only.booked_orders < only.max_orders)
                : (typeof only.booked_items === 'number' && typeof only.max_items === 'number' && only.max_items > 0)
                  ? (only.booked_items < only.max_items)
                  : true
            ))
          )
          : false;
        if (only && onlyAvailable) {
          setSelectedTimeSlot(only.slot_id);
        }
      } catch (err) {
        console.error('Error fetching time slots:', err);
        setError('שגיאה בטעינת משבצות זמן');
        setTimeSlotsEnabled(false);
        setTimeSlots([]);
      } finally {
        setLoadingTimeSlots(false);
      }
    };

    fetchSlots();
  }, [selectedDate, storeSlug]);

  const handleContinue = () => {
    if (timeSlotsEnabled && !selectedTimeSlot) {
      setError('יש לבחור משבצת זמן');
      return;
    }
    const slot = timeSlots.find(s => s.slot_id === selectedTimeSlot);
    const dateLabel = availableDates.find(d => d.value === selectedDate)?.label || selectedDate;
    onComplete({
      date: selectedDate,
      slotId: selectedTimeSlot,
      enabled: timeSlotsEnabled,
      dateLabel,
      startTime: slot?.start_time,
      endTime: slot?.end_time,
      slotLabel: slot?.label,
    });
  };

  return (
    <>
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <Calendar className="w-16 h-16 mx-auto text-mekomit-500" />
            <h2 className="text-2xl font-bold text-mekomit-900">{t.slotPickerTitle}</h2>
            <p className="text-mekomit-600">{isAppt ? 'בחר תאריך ושעה מועדפים לתור' : 'בחר תאריך ושעה מועדפים לאיסוף ההזמנה'}</p>
          </div>

          {loadingDates ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-mekomit-500" />
              <p className="text-mekomit-600 mt-2">טוען תאריכים זמינים...</p>
            </div>
          ) : availableDates.length === 0 ? (
            <div className="bg-danger-50 border border-danger-100 rounded-lg p-4 text-center">
              <p className="text-danger-700 font-semibold">אין תאריכים זמינים למשבצות זמן</p>
              <p className="text-sm text-danger-600 mt-1">אנא צור קשר עם החנות</p>
            </div>
          ) : (
            <>
              {/* Date Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-mekomit-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> {isAppt ? 'תאריך לתור' : 'תאריך איסוף'}
                </label>
                <select
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedTimeSlot('');
                    setError('');
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-mekomit-200 focus:outline-none focus:ring-2 focus:ring-mekomit-500 text-base"
                >
                  <option value="">בחר תאריך</option>
                  {availableDates.map(date => (
                    <option key={date.value} value={date.value}>{date.label}</option>
                  ))}
                </select>
              </div>

              {/* Time Slot Selection */}
              {selectedDate && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-mekomit-900 flex items-center gap-2">
                    <Clock className="w-5 h-5" /> {isAppt ? 'שעת התור' : 'שעת איסוף'} {timeSlotsEnabled && <span className="text-danger-500">*</span>}
                  </label>

                  {loadingTimeSlots ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-mekomit-500" />
                      <p className="text-mekomit-600 mt-2">טוען משבצות זמן...</p>
                    </div>
                  ) : !timeSlotsEnabled ? (
                    <div className="bg-mekomit-50 border border-mekomit-200 rounded-lg p-4 text-center">
                      <p className="text-mekomit-700">משבצות זמן לא מופעלות עבור חנות זו</p>
                      <p className="text-sm text-mekomit-600 mt-1">תוכל להמשיך ישירות לתשלום</p>
                    </div>
                  ) : timeSlots.length === 0 ? (
                    <div className="bg-danger-50 border border-danger-100 rounded-lg p-4 text-center">
                      <p className="text-danger-700">אין משבצות זמן זמינות לתאריך זה</p>
                      <p className="text-sm text-danger-600 mt-1">אנא בחר תאריך אחר</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 auto-rows-fr">
                      {timeSlots.map(slot => {
                        // Robust availability check: use explicit flag if present,
                        // else infer from capacity fields, else default to true
                        const isAvailable = (slot.available !== undefined)
                          ? !!slot.available
                          : (typeof slot.booked_orders === 'number' && typeof slot.max_orders === 'number' && slot.max_orders > 0)
                            ? (slot.booked_orders < slot.max_orders)
                            : (typeof slot.booked_items === 'number' && typeof slot.max_items === 'number' && slot.max_items > 0)
                              ? (slot.booked_items < slot.max_items)
                              : true;

                        return (
                          <div
                            key={slot.slot_id}
                            className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 ${selectedTimeSlot === slot.slot_id
                                ? 'border-mekomit-500 bg-brand-100 font-bold'
                                : isAvailable
                                  ? 'border-mekomit-200 hover:border-mekomit-400 cursor-pointer bg-brand-100'
                                  : 'border-line-strong bg-surface-muted opacity-60 cursor-not-allowed'
                              }`}
                            onClick={() => {
                              if (isAvailable) {
                                setSelectedTimeSlot(slot.slot_id);
                                setError('');
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="timeSlot"
                              value={slot.slot_id}
                              checked={selectedTimeSlot === slot.slot_id}
                              onChange={() => { }}
                              disabled={!isAvailable}
                              className="hidden"
                            />
                            <span className={`text-base font-semibold ${isAvailable ? 'text-mekomit-900' : 'text-ink-subtle'}`}>
                              {slot.start_time} - {slot.end_time}
                            </span>
                            {slot.label && <span className="text-sm text-mekomit-600">{slot.label}</span>}
                            {!isAvailable && <span className="text-sm text-danger-500 font-semibold">מלא</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-danger-50 border border-danger-100 rounded-lg p-3 text-center">
                  <p className="text-danger-700 text-sm">{error}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Fixed button at bottom */}
      <div className="flex-none border-t border-mekomit-200 bg-white px-5 py-4">
        <button
          onClick={handleContinue}
          disabled={!selectedDate || (timeSlotsEnabled && !selectedTimeSlot)}
          className="w-full bg-gradient-to-r from-success-500 to-success-500 hover:from-success-600 hover:to-success-600 disabled:from-line disabled:to-line-strong text-white font-bold py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 text-lg"
        >
          המשך להזמנה
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </>
  );
};
